import {useEffect, useMemo, useState} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";

const EMPTY_MAPPING = {
    myob_profile_id: '',
    myob_business_id: '',
    myob_contact_id: '',
};

export default function MyobMappingDialog({
    open,
    title,
    profiles,
    businessesAndAccounts,
    initialMapping,
    saving = false,
    onClose,
    onSave,
}) {
    const [mapping, setMapping] = useState(EMPTY_MAPPING);

    useEffect(() => {
        if (!open) return;
        setMapping({
            myob_profile_id: initialMapping?.myob_profile_id || '',
            myob_business_id: initialMapping?.myob_business_id || '',
            myob_contact_id: initialMapping?.myob_contact_id || '',
        });
    }, [
        open,
        initialMapping?.myob_profile_id,
        initialMapping?.myob_business_id,
        initialMapping?.myob_contact_id,
    ]);

    const businesses = useMemo(
        () => businessesAndAccounts?.[mapping.myob_profile_id] || [],
        [businessesAndAccounts, mapping.myob_profile_id]
    );
    const contacts = useMemo(
        () => businesses.find(business => business.uid === mapping.myob_business_id)?.contacts || [],
        [businesses, mapping.myob_business_id]
    );
    const complete = Boolean(
        mapping.myob_profile_id && mapping.myob_business_id && mapping.myob_contact_id
    );

    function changeProfile(value) {
        setMapping({
            myob_profile_id: value,
            myob_business_id: '',
            myob_contact_id: '',
        });
    }

    function changeBusiness(value) {
        setMapping(current => ({
            ...current,
            myob_business_id: value,
            myob_contact_id: '',
        }));
    }

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="myob-profile-label">MYOB Profile</InputLabel>
                        <Select
                            labelId="myob-profile-label"
                            label="MYOB Profile"
                            value={mapping.myob_profile_id}
                            onChange={event => changeProfile(event.target.value)}
                        >
                            {profiles.map(profile => (
                                <MenuItem key={profile.uid} value={profile.uid}>
                                    {profile.username}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" disabled={!mapping.myob_profile_id}>
                        <InputLabel id="myob-business-label">MYOB Business</InputLabel>
                        <Select
                            labelId="myob-business-label"
                            label="MYOB Business"
                            value={mapping.myob_business_id}
                            onChange={event => changeBusiness(event.target.value)}
                        >
                            {businesses.map(business => (
                                <MenuItem key={business.uid} value={business.uid}>
                                    {business.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" disabled={!mapping.myob_business_id}>
                        <InputLabel id="myob-contact-label">MYOB Contact</InputLabel>
                        <Select
                            labelId="myob-contact-label"
                            label="MYOB Contact"
                            value={mapping.myob_contact_id}
                            onChange={event => setMapping(current => ({
                                ...current,
                                myob_contact_id: event.target.value,
                            }))}
                        >
                            {contacts.map(contact => (
                                <MenuItem key={contact.uid} value={contact.uid}>
                                    {contact.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={() => onSave(mapping)}
                    disabled={!complete || saving}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
